import React, { useEffect, useRef, useState } from "react";

import { FontAwesomeIcon }
from "@fortawesome/react-fontawesome";

import
{
    faLightbulb,
    faArrowRight,
    faGripVertical
}
from "@fortawesome/free-solid-svg-icons";


const STORAGE_KEY =
    "omniscan-ai-insights-panel-position";


export default function AiInsightsPanel(
{
	isMobile=false,
    critical = 0,
    medium = 0,
    low = 0,

    onOpen,

    title = "AI Insights",

    defaultLeft = 20,
    defaultBottom = 20
})
{
    const panelRef = useRef(null);

    const dragRef = useRef(
    {
        dragging: false,

        pointerStartX: 0,
        pointerStartY: 0,

        panelStartX: 0,
        panelStartY: 0
    });

    const [position, setPosition] = useState(
    {
        x: defaultLeft,
        y: null
    });

    const total =
        Number(critical || 0) +
        Number(medium || 0) +
        Number(low || 0);


    useEffect(() =>
    {
        const savedPosition =
            localStorage.getItem(STORAGE_KEY);

        if(savedPosition)
        {
            try
            {
                const parsed =
                    JSON.parse(savedPosition);

                if(
                    typeof parsed.x === "number" &&
                    typeof parsed.y === "number"
                )
                {
                    setPosition(parsed);

                    return;
                }
            }
            catch(error)
            {
                console.warn(
                    "Could not restore AI Insights panel position.",
                    error
                );
            }
        }

        const initialisePosition = () =>
        {
            if(!panelRef.current)
            {
                return;
            }

            const panelHeight =
                panelRef.current.offsetHeight;

            setPosition(
            {
                x: defaultLeft,

                y:
                    window.innerHeight -
                    panelHeight -
                    defaultBottom
            });
        };

        requestAnimationFrame(initialisePosition);

    }, [defaultLeft, defaultBottom]);


    useEffect(() =>
    {
        const handleResize = () =>
        {
            if(!panelRef.current)
            {
                return;
            }

            const panelWidth =
                panelRef.current.offsetWidth;

            const panelHeight =
                panelRef.current.offsetHeight;

            setPosition(current =>
            ({
                x: Math.max(
                    0,
                    Math.min(
                        current.x,
                        window.innerWidth - panelWidth
                    )
                ),

                y: Math.max(
                    0,
                    Math.min(
                        current.y ?? 0,
                        window.innerHeight - panelHeight
                    )
                )
            }));
        };

        window.addEventListener(
            "resize",
            handleResize
        );

        return () =>
        {
            window.removeEventListener(
                "resize",
                handleResize
            );
        };

    }, []);


    const startDrag = (event) =>
    {
        if(!panelRef.current)
        {
            return;
        }

        event.preventDefault();

        const panelRectangle =
            panelRef.current.getBoundingClientRect();

        dragRef.current =
        {
            dragging: true,

            pointerStartX: event.clientX,
            pointerStartY: event.clientY,

            panelStartX: panelRectangle.left,
            panelStartY: panelRectangle.top
        };

        event.currentTarget.setPointerCapture(
            event.pointerId
        );
    };


    const dragPanel = (event) =>
    {
        if(
            !dragRef.current.dragging ||
            !panelRef.current
        )
        {
            return;
        }

        const panelWidth =
            panelRef.current.offsetWidth;

        const panelHeight =
            panelRef.current.offsetHeight;

        const deltaX =
            event.clientX -
            dragRef.current.pointerStartX;

        const deltaY =
            event.clientY -
            dragRef.current.pointerStartY;

        const nextX =
            dragRef.current.panelStartX +
            deltaX;

        const nextY =
            dragRef.current.panelStartY +
            deltaY;

        setPosition(
        {
            x: Math.max(
                0,
                Math.min(
                    nextX,
                    window.innerWidth - panelWidth
                )
            ),

            y: Math.max(
                0,
                Math.min(
                    nextY,
                    window.innerHeight - panelHeight
                )
            )
        });
    };


    const stopDrag = (event) =>
    {
        if(!dragRef.current.dragging)
        {
            return;
        }

        dragRef.current.dragging = false;

        try
        {
            event.currentTarget.releasePointerCapture(
                event.pointerId
            );
        }
        catch(error)
        {
            // Pointer capture may already have been released.
        }

        setPosition(current =>
        {
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify(current)
            );

            return current;
        });
    };


    const openInsights = () =>
    {
        if(onOpen)
        {
            onOpen();
        }
    };


    if(total === 0)
    {
        return null;
    }


	const panelStyle =
	{
	    position:
	        isMobile
	            ? "relative"
	            : "fixed",

	    left:
	        isMobile
	            ? "auto"
	            : position.x + "px",

	    top:
	        isMobile
	            ? "auto"
	            :
	            (
	                position.y === null
	                    ? "auto"
	                    : position.y + "px"
	            ),

	    bottom:
	        isMobile
	            ? "auto"
	            :
	            (
	                position.y === null
	                    ? defaultBottom + "px"
	                    : "auto"
	            ),

	    width:
	        isMobile
	            ? "calc(100% - 20px)"
	            : "310px",

	    margin:
	        isMobile
	            ? "15px 10px 20px 10px"
	            : "0",

	    boxSizing:
	        "border-box",

	    background:
	        "rgba(255,255,255,.42)",

	    backdropFilter:
	        "blur(22px)",

	    WebkitBackdropFilter:
	        "blur(22px)",

	    border:
	        "1px solid rgba(255,255,255,.72)",

	    borderRadius:
	        "0px",

	    boxShadow:
	        isMobile
	            ? "0 6px 18px rgba(16,24,40,.14)"
	            : "0 16px 42px rgba(16,24,40,.24)",

	    overflow:
	        "hidden",

	    zIndex:
	        isMobile
	            ? 1
	            : 9999,

	    userSelect:
	        "none"
	};


    const headerStyle =
    {
        display: "flex",

        alignItems: "center",
        justifyContent: "space-between",

        height: "38px",

        padding: "0 12px",

        borderBottom:
            "1px solid rgba(80,92,110,.14)",

        cursor: "grab",

        touchAction: "none"
    };


    const titleContainerStyle =
    {
        display: "flex",

        alignItems: "center",

        gap: "8px"
    };


	const iconStyle =
	{
	    color: "#45afe0",
	    fontSize: "14px"
	};


    const titleStyle =
    {
        color: "#26313D",

        fontSize: "13px",

        fontWeight: 700,

        letterSpacing: ".1px"
    };


    const gripStyle =
    {
        color: "rgba(38,49,61,.4)",

        fontSize: "13px"
    };


    const contentStyle =
    {
        display: "flex",

        alignItems: "center",
        justifyContent: "space-between",

        gap: "12px",

        padding: "12px"
    };


    const ragContainerStyle =
    {
        display: "flex",

        alignItems: "center",

        gap: "8px"
    };


    const buildRagBoxStyle = (gradient) =>
    ({
        width: "46px",
        height: "46px",

        display: "flex",

        flexDirection: "column",

        alignItems: "center",
        justifyContent: "center",

        background: gradient,

        borderRadius: "5px",

        boxShadow:
            "inset 0 1px 0 rgba(255,255,255,.28), " +
            "0 5px 12px rgba(18,24,32,.16)",

        color: "white"
    });


    const ragNumberStyle =
    {
        fontSize: "18px",

        fontWeight: 800,

        lineHeight: "18px"
    };


    const ragLabelStyle =
    {
        marginTop: "3px",

        fontSize: "8px",

        fontWeight: 700,

        letterSpacing: ".45px",

        textTransform: "uppercase",

        opacity: .9
    };


	const openButtonStyle =
	{
	    height: "46px",

	    display: "flex",

	    alignItems: "center",

	    justifyContent: "center",

	    gap: "8px",

	    padding: "0 13px",

	    //--------------------------------------------------------
	    // Light blue action accent
	    //--------------------------------------------------------

	    background:
	        "linear-gradient(180deg, #45afe0 0%, #2695cc 100%)",

	    border:
	        "none",

	    borderRadius:
	        "5px",

	    boxShadow:
	        "inset 0 1px 0 rgba(255,255,255,.30), " +
	        "0 5px 12px rgba(38,149,204,.24)",

	    color:
	        "#ffffff",

	    cursor:
	        "pointer",

	    fontSize:
	        "11px",

	    fontWeight:
	        700,

	    whiteSpace:
	        "nowrap"
	};


    return (

        <div
            ref={panelRef}

            style={panelStyle}
        >

		<div
		    style={{
		        ...headerStyle,
		        cursor: isMobile ? "default" : "grab"
		    }}

		    onPointerDown={
		        isMobile
		            ? undefined
		            : startDrag
		    }

		    onPointerMove={
		        isMobile
		            ? undefined
		            : dragPanel
		    }

		    onPointerUp={
		        isMobile
		            ? undefined
		            : stopDrag
		    }

		    onPointerCancel={
		        isMobile
		            ? undefined
		            : stopDrag
		    }
		>

                <div style={titleContainerStyle}>

                    <FontAwesomeIcon
                        icon={faLightbulb}
                        style={iconStyle}
                    />

                    <span style={titleStyle}>
                        {title}
                    </span>

                </div>

                <FontAwesomeIcon
                    icon={faGripVertical}
                    style={gripStyle}
                />

            </div>


            <div style={contentStyle}>

                <div style={ragContainerStyle}>

                    <div
                        style={buildRagBoxStyle(
                            "linear-gradient(180deg,#F15B72 0%,#C91F42 100%)"
                        )}
                    >

                        <div style={ragNumberStyle}>
                            {critical}
                        </div>

                        <div style={ragLabelStyle}>
                            Critical
                        </div>

                    </div>


                    <div
                        style={buildRagBoxStyle(
                            "linear-gradient(180deg,#F8BC4C 0%,#E58B0A 100%)"
                        )}
                    >

                        <div style={ragNumberStyle}>
                            {medium}
                        </div>

                        <div style={ragLabelStyle}>
                            Medium
                        </div>

                    </div>


                    <div
                        style={buildRagBoxStyle(
                            "linear-gradient(180deg,#42C98A 0%,#23865A 100%)"
                        )}
                    >

                        <div style={ragNumberStyle}>
                            {low}
                        </div>

                        <div style={ragLabelStyle}>
                            Low
                        </div>

                    </div>

                </div>


                <button
                    type="button"

                    style={openButtonStyle}

                    onClick={openInsights}
                >

                    View

                    <FontAwesomeIcon
                        icon={faArrowRight}
                    />

                </button>

            </div>

        </div>
    );
}